import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Select from "react-select";
import { Switch } from "@mui/material";
import { FaTrashAlt } from "react-icons/fa";
import { message } from "antd";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./AddItem.css";
import APIURL, { IMAGE_BASE_URL } from "../api/apiClient";
import Loader from "../components/Loader";
import VariantEditorSection from "../components/VariantEditorSection";
import {
  buildVariantPayload,
  createEmptyVariant,
  hydrateVariants,
  normalizeVariantList,
  updateVariantDraft,
  validateVariants,
} from "../utils/itemVariants";

const initialValues = {
  name: "",
  brand: "",
  image: "",
  imageFile: null,
  thumbnailPreview: "",
  images: [],
  imageFiles: [],
  imagePreviews: [],
  description: "",
  category_id: "",
  category_ids: [],
  actual_price: "",
  selling_price: "",
  tax: "",
  discount: "",
  status: 1,
  stock: "",
  maximum_cart_quantity: "",
  is_approved: "",
  refurbished: "",
  net_weight: "",
  dimensions: "",
};

const parseCategoryIds = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    } catch (error) {
      return value
        .split(",")
        .map((entry) => Number(entry.trim()))
        .filter((entry) => !Number.isNaN(entry));
    }
  }

  if (typeof value === "number") {
    return [value];
  }

  return [];
};

const EditScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileImageRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState(initialValues);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [variants, setVariants] = useState([]);
  const [variantErrors, setVariantErrors] = useState({});
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState("");

  const calculateDiscount = (actual, selling) => {
    if (!actual || Number(actual) <= 0 || !selling) {
      return "";
    }

    return Math.round(((Number(actual) - Number(selling)) / Number(actual)) * 100).toString();
  };

  const calculateSellingPrice = (actual, discount) => {
    if (!actual || Number(actual) <= 0 || discount === "") {
      return "";
    }

    return (Number(actual) - (Number(actual) * Number(discount)) / 100).toFixed(2);
  };

  const validate = () => {
    const newErrors = {};

    if (!values.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!values.brand.trim()) {
      newErrors.brand = "Brand is required";
    }

    if (!selectedCategory) {
      newErrors.category = "Please select a main category";
    }

    if (selectedCategory && subCategories.length > 0 && !selectedSubCategory) {
      newErrors.category_ids = "Please select a sub category";
    }

    if (!values.actual_price || Number(values.actual_price) <= 0) {
      newErrors.actual_price = "Actual price must be a positive number";
    }

    if (!values.selling_price || Number(values.selling_price) <= 0) {
      newErrors.selling_price = "Selling price must be a positive number";
    }

    if (values.tax === "" || Number.isNaN(Number(values.tax))) {
      newErrors.tax = "Please enter a valid tax percentage";
    }

    if (values.discount === "" || Number.isNaN(Number(values.discount))) {
      newErrors.discount = "Please enter a valid discount";
    }

    const currentVariantErrors = validateVariants(variants);

    setErrors(newErrors);
    setVariantErrors(currentVariantErrors);

    return Object.keys(newErrors).length === 0 && Object.keys(currentVariantErrors).length === 0;
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await APIURL.get("/fetchcategories.php");
        const parsed = Array.isArray(response) ? response : JSON.parse(response);

        setCategories(
          parsed.map((category) => ({
            value: category.id,
            label: category.name,
          }))
        );
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchItem = async () => {
      setLoading(true);

      try {
        const response = await APIURL.get(`/fetchitemid.php?id=${id}`);
        let imageList = [];
        const {
          variants: itemVariants = [],
          images: responseImages,
          image: responseImage,
          category_ids: rawCategoryIds,
          ...itemFields
        } = response;

        if (responseImages) {
          try {
            const decodedImages = typeof responseImages === "string" ? JSON.parse(responseImages) : responseImages;
            imageList = Array.isArray(decodedImages) ? decodedImages : [];
          } catch (error) {
            console.error("Error parsing item images:", error);
          }
        }

        const parsedCategoryIds = parseCategoryIds(rawCategoryIds);

        setValues({
          ...initialValues,
          ...itemFields,
          image: responseImage ? `${IMAGE_BASE_URL}${responseImage}` : "",
          imageFile: null,
          thumbnailPreview: "",
          images: imageList.map((image) => `${IMAGE_BASE_URL}${image}`),
          imageFiles: [],
          imagePreviews: [],
          category_id: itemFields.category_id || "",
          category_ids: parsedCategoryIds,
        });
        setVariants(hydrateVariants(itemVariants, IMAGE_BASE_URL));
        setVariantErrors({});
        setErrors({});
        setErrorMessage("");
      } catch (error) {
        console.error("Error fetching item:", error);
        setErrorMessage("Unable to load item details.");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchItem();
    }
  }, [id]);

  useEffect(() => {
    if (!selectedCategory && values.category_id && categories.length > 0) {
      const mainCategory = categories.find((category) => Number(category.value) === Number(values.category_id));
      if (mainCategory) {
        setSelectedCategory(mainCategory);
      }
    }
  }, [categories, selectedCategory, values.category_id]);

  useEffect(() => {
    if (!selectedCategory) {
      setSubCategories([]);
      setSelectedSubCategory(null);
      return;
    }

    const fetchSubCategories = async () => {
      try {
        const response = await APIURL.get(`/subCategories.php?category_id=${selectedCategory.value}`);

        if (response.status && Array.isArray(response.data)) {
          const nextSubCategories = response.data.map((subCategory) => ({
            value: subCategory.id,
            label: subCategory.name,
          }));

          setSubCategories(nextSubCategories);

          const currentSubCategoryId = parseCategoryIds(values.category_ids)[0];
          if (currentSubCategoryId) {
            const activeSubCategory = nextSubCategories.find(
              (subCategory) => Number(subCategory.value) === Number(currentSubCategoryId)
            );
            setSelectedSubCategory(activeSubCategory || null);
          } else {
            setSelectedSubCategory(null);
          }
        } else {
          setSubCategories([]);
          setSelectedSubCategory(null);
        }
      } catch (error) {
        console.error("Error fetching subcategories:", error);
        setSubCategories([]);
        setSelectedSubCategory(null);
      }
    };

    fetchSubCategories();
  }, [selectedCategory, values.category_ids]);

  const handleMainCategoryChange = (option) => {
    setSelectedCategory(option);
    setSelectedSubCategory(null);
    setValues((previous) => ({
      ...previous,
      category_id: option ? option.value : "",
      category_ids: [],
    }));
  };

  const handleSubCategoryChange = (option) => {
    setSelectedSubCategory(option);
    setValues((previous) => ({
      ...previous,
      category_ids: option ? [option.value] : [],
    }));
  };

  const handleChange = (event) => {
    const { name, type, checked, files, value } = event.target;

    if (type === "checkbox") {
      setValues((previous) => ({
        ...previous,
        [name]: checked ? 1 : 0,
      }));
      return;
    }

    if (type === "file") {
      if (name === "image" && files?.[0]) {
        const file = files[0];
        setValues((previous) => ({
          ...previous,
          imageFile: file,
          thumbnailPreview: URL.createObjectURL(file),
        }));
      }

      if (name === "images") {
        const selectedFiles = Array.from(files || []);
        setValues((previous) => ({
          ...previous,
          ...(function () {
            const remainingSlots = Math.max(0, 5 - previous.images.length - previous.imageFiles.length);
            const acceptedFiles = selectedFiles.slice(0, remainingSlots);
            return {
              imageFiles: [...previous.imageFiles, ...acceptedFiles],
              imagePreviews: [
                ...previous.imagePreviews,
                ...acceptedFiles.map((file) => URL.createObjectURL(file)),
              ],
            };
          })(),
        }));

        if (fileImageRef.current) {
          fileImageRef.current.value = "";
        }
      }

      return;
    }

    setValues((previous) => {
      const updated = { ...previous, [name]: value };

      if (name === "actual_price" || name === "selling_price") {
        updated.discount = calculateDiscount(updated.actual_price, updated.selling_price);
      }

      if (name === "discount") {
        const discountNumber = Number(value);
        updated.discount = Number.isFinite(discountNumber) ? Math.round(discountNumber).toString() : "";
      }

      if (name === "actual_price" || name === "discount") {
        updated.selling_price = calculateSellingPrice(updated.actual_price, updated.discount);
      }

      return updated;
    });
  };

  const handleDescriptionChange = (content) => {
    setValues((previous) => ({
      ...previous,
      description: content,
    }));
  };

  const handleDeleteImage = (index) => {
    setValues((previous) => {
      if (index < previous.images.length) {
        return {
          ...previous,
          images: previous.images.filter((_, imageIndex) => imageIndex !== index),
        };
      }

      const previewIndex = index - previous.images.length;
      return {
        ...previous,
        imageFiles: previous.imageFiles.filter((_, imageIndex) => imageIndex !== previewIndex),
        imagePreviews: previous.imagePreviews.filter((_, imageIndex) => imageIndex !== previewIndex),
      };
    });
  };

  const updateVariantsState = (updater) => {
    setVariants((previous) => normalizeVariantList(updater(previous)));
    setVariantErrors({});
  };

  const handleAddVariant = () => {
    updateVariantsState((previous) => [
      ...previous,
      createEmptyVariant({
        is_default: previous.length === 0 ? 1 : 0,
        sort_order: String(previous.length + 1),
      }),
    ]);
  };

  const handleRemoveVariant = (clientId) => {
    updateVariantsState((previous) => previous.filter((variant) => variant.client_id !== clientId));
  };

  const handleVariantFieldChange = (clientId, field, value) => {
    updateVariantsState((previous) =>
      previous.map((variant) =>
        variant.client_id === clientId ? updateVariantDraft(variant, field, value) : variant
      )
    );
  };

  const handleVariantImageChange = (clientId, file) => {
    updateVariantsState((previous) =>
      previous.map((variant) =>
        variant.client_id === clientId
          ? {
              ...variant,
              image: file,
              imagePreview: file ? URL.createObjectURL(file) : variant.imagePreview,
            }
          : variant
      )
    );
  };

  const handleClearVariantImage = (clientId) => {
    updateVariantsState((previous) =>
      previous.map((variant) =>
        variant.client_id === clientId
          ? {
              ...variant,
              image: null,
              existing_image: "",
              imagePreview: "",
            }
          : variant
      )
    );
  };

  const handleVariantGalleryChange = (clientId, files) => {
    updateVariantsState((previous) =>
      previous.map((variant) => {
        if (variant.client_id !== clientId) {
          return variant;
        }

        const remainingSlots = Math.max(
          0,
          5 - variant.existing_images.length - variant.galleryFiles.length
        );
        const acceptedFiles = files.slice(0, remainingSlots);

        return {
          ...variant,
          galleryFiles: [...variant.galleryFiles, ...acceptedFiles],
          galleryPreviews: [
            ...variant.galleryPreviews,
            ...acceptedFiles.map((file) => URL.createObjectURL(file)),
          ],
        };
      })
    );
  };

  const handleDeleteVariantGalleryImage = (clientId, imageIndex) => {
    updateVariantsState((previous) =>
      previous.map((variant) => {
        if (variant.client_id !== clientId) {
          return variant;
        }

        if (imageIndex < variant.existing_images.length) {
          return {
            ...variant,
            existing_images: variant.existing_images.filter((_, index) => index !== imageIndex),
            galleryImages: variant.galleryImages.filter((_, index) => index !== imageIndex),
          };
        }

        const previewIndex = imageIndex - variant.existing_images.length;
        return {
          ...variant,
          galleryFiles: variant.galleryFiles.filter((_, index) => index !== previewIndex),
          galleryPreviews: variant.galleryPreviews.filter((_, index) => index !== previewIndex),
        };
      })
    );
  };

  const handleSetDefaultVariant = (clientId) => {
    updateVariantsState((previous) =>
      previous.map((variant) => ({
        ...variant,
        is_default: variant.client_id === clientId ? 1 : 0,
      }))
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!id || !validate()) {
      return;
    }

    try {
      const requestData = new FormData();
      requestData.append("id", id);

      const existingImages = values.images.map((imageUrl) => imageUrl.replace(IMAGE_BASE_URL, ""));
      requestData.append("existing_images", JSON.stringify(existingImages));

      if (values.imageFile instanceof File) {
        requestData.append("image", values.imageFile);
      }

      values.imageFiles.forEach((file) => {
        requestData.append("images[]", file);
      });

      Object.entries(values).forEach(([key, value]) => {
        if (
          [
            "image",
            "imageFile",
            "thumbnailPreview",
            "images",
            "imageFiles",
            "imagePreviews",
            "category_id",
            "category_ids",
          ].includes(key)
        ) {
          return;
        }

        requestData.append(key, value ?? "");
      });

      requestData.append("category_id", selectedCategory ? selectedCategory.value : values.category_id);
      requestData.append(
        "category_ids",
        JSON.stringify(selectedSubCategory ? [selectedSubCategory.value] : [])
      );

      const variantPayload = buildVariantPayload(variants);
      variantPayload.forEach((variantPayloadItem) => {
        const matchingVariant = variants.find(
          (variant) => variant.client_id === variantPayloadItem.client_id
        );

        if (variantPayloadItem.image_upload_key && matchingVariant?.image instanceof File) {
          requestData.append(variantPayloadItem.image_upload_key, matchingVariant.image);
        }

        (variantPayloadItem.gallery_upload_keys || []).forEach((uploadKey, imageIndex) => {
          const galleryFile = matchingVariant?.galleryFiles?.[imageIndex];
          if (galleryFile instanceof File) {
            requestData.append(uploadKey, galleryFile);
          }
        });
      });

      requestData.append(
        "variants",
        JSON.stringify(variantPayload.map(({ client_id, ...variant }) => variant))
      );

      const response = await APIURL.post("/updateitems.php", requestData, true);

      if (response.success) {
        message.success("Item Updated Successfully");
        navigate("/items");
        return;
      }

      setErrorMessage(response.error || response.message || "An error occurred while updating the item.");
    } catch (error) {
      console.error("Error during submission:", error);
      setErrorMessage(error.message || "An error occurred while updating the item.");
    }
  };

  if (loading) {
    return <Loader type="full" loading={loading} />;
  }

  return (
    <div className="form-container">
      <h2 className="form-title">Edit Item</h2>
      <form onSubmit={handleSubmit} encType="multipart/form-data" className="form-group">
        <div className="form-grid">
          <div className="form-fields">
            <div className="name-brand">
              <label htmlFor="name" className="label">
                Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                placeholder="Enter name"
                className="form-control input"
                value={values.name}
                onChange={handleChange}
                required
              />
              {errors.name ? <div className="error-message">{errors.name}</div> : null}

              <label htmlFor="brand" className="label">
                Brand
              </label>
              <input
                type="text"
                id="brand"
                name="brand"
                placeholder="Enter your brand"
                className="form-control input"
                value={values.brand}
                onChange={handleChange}
                required
              />
              {errors.brand ? <div className="error-message">{errors.brand}</div> : null}
            </div>

            <label htmlFor="description" className="label">
              Description
            </label>
            <ReactQuill
              theme="snow"
              value={values.description}
              onChange={handleDescriptionChange}
              placeholder="Enter description"
              style={{ marginBottom: "50px" }}
            />

            <label htmlFor="category" className="label">
              Main Category
            </label>
            <Select
              name="category_id"
              value={selectedCategory}
              onChange={handleMainCategoryChange}
              options={categories}
              placeholder="Select Main Category"
            />
            {errors.category ? <div className="error-message">{errors.category}</div> : null}

            <label htmlFor="category_ids" className="label">
              Sub Category
            </label>
            <Select
              value={selectedSubCategory}
              onChange={handleSubCategoryChange}
              options={subCategories}
              placeholder={selectedCategory ? "Select Sub Category" : "Select Main Category First"}
              isDisabled={!selectedCategory}
            />
            {errors.category_ids ? <div className="error-message">{errors.category_ids}</div> : null}

            <label htmlFor="actual_price" className="label">
              Actual Price
            </label>
            <input
              type="number"
              id="actual_price"
              name="actual_price"
              placeholder="Enter actual price"
              className="form-control input"
              value={values.actual_price}
              onChange={handleChange}
              required
            />
            {errors.actual_price ? <div className="error-message">{errors.actual_price}</div> : null}

            <label htmlFor="selling_price" className="label">
              Selling Price
            </label>
            <input
              type="number"
              id="selling_price"
              name="selling_price"
              placeholder="Enter selling price"
              className="form-control input"
              value={values.selling_price}
              onChange={handleChange}
              required
            />
            {errors.selling_price ? (
              <div className="error-message">{errors.selling_price}</div>
            ) : null}

            <label htmlFor="tax" className="label">
              Tax Percentage (%)
            </label>
            <input
              type="number"
              id="tax"
              name="tax"
              placeholder="Enter tax percentage"
              className="form-control input"
              value={values.tax}
              onChange={handleChange}
              required
            />
            {errors.tax ? <div className="error-message">{errors.tax}</div> : null}

            <div className="switch-container">
              <span className="switch-label">Status</span>
              <Switch
                checked={Boolean(values.status)}
                onChange={(event) =>
                  handleChange({
                    target: {
                      name: "status",
                      type: "checkbox",
                      checked: event.target.checked,
                    },
                  })
                }
              />
            </div>

            <label htmlFor="discount" className="label">
              Discount (%)
            </label>
            <input
              type="number"
              id="discount"
              name="discount"
              placeholder="Enter discount"
              className="form-control input"
              value={values.discount}
              onChange={handleChange}
              required
            />
            {errors.discount ? <div className="error-message">{errors.discount}</div> : null}
          </div>

          <div className="form-sidebar">
            <div className="image-upload-card">
              <h3 className="section-title">Images</h3>

              <div className="thumbnail-upload">
                <label htmlFor="thumbnail" className="label-thumbnail">
                  Add <br />
                  Thumbnail
                  <input type="file" id="thumbnail" name="image" accept="image/*" onChange={handleChange} />
                </label>
                {values.image || values.thumbnailPreview ? (
                  <div className="thumbnail-preview">
                    <img
                      src={values.thumbnailPreview || values.image}
                      alt="thumbnail preview"
                      className="thumbnail-img"
                    />
                  </div>
                ) : null}
              </div>

              <div className="additional-images">
                <div className="images-upload-container">
                  <label className="images-label" htmlFor="images">
                    + Add Images <br /> <span>Up to 5 images</span>
                    <input
                      type="file"
                      name="images"
                      accept="image/*"
                      id="images"
                      onChange={handleChange}
                      ref={fileImageRef}
                      multiple
                    />
                  </label>

                  <div className="images-preview">
                    {[...values.images, ...values.imagePreviews].map((image, index) => (
                      <div key={`${image}-${index}`} className="image-preview-item">
                        <img src={image} alt={`Gallery ${index + 1}`} className="preview-img" />
                        <button
                          type="button"
                          onClick={() => handleDeleteImage(index)}
                          className="delete-button"
                        >
                          <FaTrashAlt className="trash-icon" />
                        </button>
                        <span className="image-number">{index + 1}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="additional-fields-card">
              <h3 className="section-title">Additional Details</h3>

              <label htmlFor="stock" className="label">
                Stock
              </label>
              <input
                type="number"
                id="stock"
                name="stock"
                placeholder="Enter stock"
                className="form-control input"
                value={values.stock}
                onChange={handleChange}
              />

              <label htmlFor="maximum_cart_quantity" className="label">
                Maximum Order Quantity
              </label>
              <input
                type="number"
                id="maximum_cart_quantity"
                name="maximum_cart_quantity"
                placeholder="Enter MOQ"
                className="form-control input"
                value={values.maximum_cart_quantity}
                onChange={handleChange}
              />

              <label htmlFor="is_approved" className="label">
                Approved
              </label>
              <select
                id="is_approved"
                name="is_approved"
                className="custom-select select"
                value={values.is_approved}
                onChange={handleChange}
              >
                <option value="" disabled>
                  Select Option
                </option>
                <option value="1">Yes</option>
                <option value="0">No</option>
              </select>

              <label htmlFor="refurbished" className="label">
                Refurbished
              </label>
              <select
                id="refurbished"
                name="refurbished"
                className="custom-select select"
                value={values.refurbished}
                onChange={handleChange}
              >
                <option value="" disabled>
                  Select Option
                </option>
                <option value="1">Yes</option>
                <option value="0">No</option>
              </select>

              <label htmlFor="net_weight" className="label">
                Net Weight
              </label>
              <input
                type="text"
                id="net_weight"
                name="net_weight"
                placeholder="Enter Net Weight"
                className="form-control input"
                value={values.net_weight}
                onChange={handleChange}
              />

              <label htmlFor="dimensions" className="label">
                Dimensions
              </label>
              <input
                type="text"
                id="dimensions"
                name="dimensions"
                placeholder="Enter Dimensions"
                className="form-control input"
                value={values.dimensions}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        <VariantEditorSection
          variants={variants}
          errors={variantErrors}
          onAddVariant={handleAddVariant}
          onRemoveVariant={handleRemoveVariant}
          onVariantFieldChange={handleVariantFieldChange}
          onVariantImageChange={handleVariantImageChange}
          onVariantGalleryChange={handleVariantGalleryChange}
          onDeleteVariantGalleryImage={handleDeleteVariantGalleryImage}
          onClearVariantImage={handleClearVariantImage}
          onSetDefaultVariant={handleSetDefaultVariant}
        />

        {errorMessage ? <div className="error-message">{errorMessage}</div> : null}

        <button type="submit" name="submit" className="btn btn-primary submit-button">
          Update
        </button>
      </form>
    </div>
  );
};

export default EditScreen;
